import React, { useState } from "react";
import { Button, Icon } from "semantic-ui-react";
import { useLanguage } from "../../../../contexts";
import { P, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { Notice, SaveBar, SaveErrors, Section, StatusBadge } from "../common/SectionCommon";
import "../common/common.scss";

const FRECUENCIAS = ["Mensual", "Trimestral", "Semestral", "Anual"];
const tomar = (inst) => ({ idens: inst.idens || [] });
const fmt = (v, d = 0) => P.fmt(v, d);
const decimales = (v) => (Math.abs(v) < 1 ? 3 : Math.abs(v) < 10 ? 2 : 1);

// Valor actual de un IDEn elegido (renderIden del prototipo). null = no aplica a esta planta.
function valorIden(f, d, t) {
  if (d.op) {
    const o = (f.operations || []).find((x) => x.nombre === d.op);
    if (!o) return null;
    const ev = P.evalOperacion(o);
    return {
      nivel: t("energy_performance_process"), nombre: o.nombre, metodo: "Ratio", formula: `${t("energy_performance_iden_process_formula")} ÷ ${o.unidad || "u"}`,
      valor: `${fmt(ev.esp, decimales(ev.esp))} kWh/${o.unidad || "u"}`, complementario: false,
    };
  }
  const L = P.IDEN_LIB[d.key];
  if (!L || (L.solo && !L.solo(f))) return null;
  const v = L.calc(f);
  return {
    nivel: L.nivel, nombre: L.nombre(f), metodo: L.metodo, formula: L.formula(f),
    valor: Number.isFinite(v) ? `${fmt(v, decimales(v))} ${L.unidad(f)}` : t("energy_performance_pending"), complementario: !!L.complementario,
  };
}

// Indicadores de desempeño energético (IDEn) — cláusula 6.4 (hoja 01 filas 46, 74, 148, 163; hoja 21).
export function EnpiScreen({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  const [elegido, setElegido] = useState("");
  const [frecuencia, setFrecuencia] = useState("Mensual");

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;
  if (!f) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>;

  const E = P.energiaAnual(f);
  const prod = P.prodAnual(f);
  const mix = P.idnSplitElec(f);
  const usados = new Set(b.idens.map((d) => (d.key ? `k:${d.key}` : `o:${d.op}`)));
  const opciones = [
    ...Object.keys(P.IDEN_LIB).filter((k) => !usados.has(`k:${k}`) && (!P.IDEN_LIB[k].solo || P.IDEN_LIB[k].solo(f)))
      .map((k) => [`k:${k}`, `${P.IDEN_LIB[k].nivel} · ${P.IDEN_LIB[k].nombre(f)}${P.IDEN_LIB[k].complementario ? ` (${t("energy_performance_iden_complementary")})` : ""}`]),
    ...(f.operations || []).filter((o) => !usados.has(`o:${o.nombre}`)).map((o) => [`o:${o.nombre}`, `${t("energy_performance_process")} · ${o.nombre} (${t("energy_performance_iden_process_formula")} ÷ ${o.unidad || "u"})`]),
  ];
  const filas = b.idens.map((d, i) => ({ d, i, v: valorIden(f, d, t) }));
  const deDesempeno = filas.filter((x) => x.v && !x.v.complementario).length;

  const agregar = () => {
    const v = elegido || (opciones[0] && opciones[0][0]);
    if (!v) return;
    const d = v.startsWith("k:") ? { key: v.slice(2) } : { op: v.slice(2) };
    d.frecuencia = frecuencia;
    cambiar("idens", [...b.idens, d]);
    setElegido("");
  };
  const set = (i, clave, valor) => cambiar("idens", b.idens.map((d, j) => (j === i ? { ...d, [clave]: valor } : d)));

  return (
    <div className="ep-screen">
      <h3 className="ep-screen__title">{t("energy_performance_iden_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_iden_intro")}</p>

      <div className="ep-kpis">
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_iden_intensity")}</div><div className="ep-kpi__value">{prod > 0 ? fmt(E / prod, E / prod < 1 ? 3 : 2) : "—"}</div><div className="ep-kpi__sub">kWh/{f.unidadProdCorta}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">EUI</div><div className="ep-kpi__value">{f.area > 0 ? fmt(E / f.area, 1) : "—"}</div><div className="ep-kpi__sub">kWh/m²·{t("energy_performance_year")}</div></div>
        <div className="ep-kpi">
          <div className="ep-kpi__label">{t("energy_performance_iden_mix")}</div>
          <div className="ep-kpi__value">{mix.e + mix.t > 0 ? `${fmt(mix.e, 0)}%` : "—"}</div>
          <div className="ep-kpi__sub">{mix.e + mix.t > 0 ? `${t("energy_performance_iden_electric")} · ${fmt(mix.t, 0)}% ${t("energy_performance_iden_thermal")}` : t("energy_performance_iden_mix_pending")}</div>
        </div>
      </div>

      {deDesempeno === 0 && (
        <Notice tipo="danger" titulo="6.4 — Brecha mayor">{t("energy_performance_iden_none")}</Notice>
      )}
      {f._avisoIdenOp && <Notice>{t("energy_performance_iden_dropped").replace("{n}", f._avisoIdenOp)}</Notice>}

      <Section title={t("energy_performance_iden_panel")} note={t("energy_performance_iden_panel_note")}>
        <div className="ep-table">
          <table>
            <thead>
              <tr>
                <th>{t("energy_performance_iden_level")}</th><th>{t("energy_performance_iden_indicator")}</th><th>{t("energy_performance_iden_method")}</th><th>{t("energy_performance_iden_formula")}</th>
                <th>{t("energy_performance_iden_value")}</th><th>{t("energy_performance_iden_frequency")}</th><th>{t("energy_performance_iden_use")}</th><th aria-label={t("energy_performance_actions")} />
              </tr>
            </thead>
            <tbody>
              {!filas.length && <tr><td colSpan={8} className="ep-table__empty">{t("energy_performance_iden_empty")}</td></tr>}
              {filas.map(({ d, i, v }) => (
                <tr key={i}>
                  {v ? (
                    <>
                      <td>{v.nivel}</td>
                      <td>{v.nombre}{v.complementario && <div><StatusBadge estado="Pendiente">{t("energy_performance_iden_complementary")}</StatusBadge></div>}</td>
                      <td>{v.metodo}</td>
                      <td className="ep-section__note">{v.formula}</td>
                      <td className="ep-mono">{v.valor}</td>
                    </>
                  ) : (
                    <td colSpan={5} className="ep-section__note">{d.key || d.op}: {t("energy_performance_iden_not_applicable")}</td>
                  )}
                  <td>
                    <select aria-label={`${t("energy_performance_iden_frequency")} ${i + 1}`} value={d.frecuencia || "Mensual"} onChange={(e) => set(i, "frecuencia", e.target.value)}>
                      {FRECUENCIAS.map((x) => <option key={x}>{x}</option>)}
                    </select>
                  </td>
                  <td><input type="text" aria-label={`${t("energy_performance_iden_use")} ${i + 1}`} value={d.uso || ""} placeholder={t("energy_performance_iden_use_placeholder")} onChange={(e) => set(i, "uso", e.target.value)} /></td>
                  <td><Button type="button" icon basic size="mini" aria-label={t("energy_performance_delete_row")} onClick={() => cambiar("idens", b.idens.filter((_, j) => j !== i))}><Icon name="trash alternate outline" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {opciones.length > 0 ? (
          <div className="ep-pills ep-iden__add">
            <select aria-label={t("energy_performance_iden_choose")} value={elegido} onChange={(e) => setElegido(e.target.value)}>
              <option value="">{t("energy_performance_iden_choose")}</option>
              {opciones.map(([v, txt]) => <option key={v} value={v}>{txt}</option>)}
            </select>
            <select aria-label={t("energy_performance_iden_frequency")} value={frecuencia} onChange={(e) => setFrecuencia(e.target.value)}>
              {FRECUENCIAS.map((x) => <option key={x}>{x}</option>)}
            </select>
            <Button type="button" basic size="small" disabled={!elegido} onClick={agregar}><Icon name="plus" /> {t("energy_performance_iden_add")}</Button>
          </div>
        ) : <p className="ep-section__note">{t("energy_performance_iden_all_added")}</p>}
        <p className="ep-section__note">{t("energy_performance_iden_min_note")}</p>
      </Section>

      <Section title={t("energy_performance_iden_normalization")} note={t("energy_performance_iden_normalization_note")} />

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={0} saving={saving} onSave={() => guardar(b)} onDiscard={descartar} t={t} />
    </div>
  );
}
